<template>
    <div
        role="button"
        tabindex="0"
        class="relative rounded-xl border px-6 py-4 shadow-sm cursor-pointer transition-colors text-left w-full flex flex-col"
        :class="
            isSelected
                ? 'bg-valence-primary-100 border-[#e2e8f0]'
                : isLinkedHover
                  ? 'bg-slate-50 border-valence-grey-300'
                  : 'bg-white border-[#e2e8f0] hover:bg-slate-50 hover:border-valence-grey-300'
        "
        @click="handleCardActivate"
        @keydown.enter="handleCardActivate"
        @keydown.space.prevent="handleCardActivate">
        <!-- Suggested badge -->
        <div v-if="isSuggestion" class="flex items-center gap-1 self-start mb-1 text-valence-primary-500">
            <span class="text-[11px] font-semibold uppercase tracking-wide">{{ suggestionLabel }}</span>
            <i class="bi bi-stars text-xs" aria-hidden="true"></i>
        </div>

        <!-- Title row -->
        <div class="flex items-center gap-2 min-w-0">
            <span class="details text-valence-grey-900 truncate flex-1 min-w-0">{{ goal.title }}</span>
        </div>

        <!-- Bottom row: action text (+progress bar) on the left, CTAs on the right, inline -->
        <div class="mt-2 flex items-center justify-between gap-3">
            <div class="flex flex-col gap-1 min-w-0 flex-1">
                <div class="flex items-center gap-1.5 min-w-0">
                    <p class="details text-valence-grey-750 truncate">{{ actionLabel }}</p>
                </div>
                <BaseProgressBar class="[--progress-height:0.1875rem]" :class="{ invisible: totalActions === 0 }" :value="completedActions" :max="totalActions" size="xs" />
            </div>
            <div v-if="hasCta" class="flex items-center gap-2 shrink-0">
                <!-- Assign CTA: opens the assign-to-team-member modal (never the goal sidebar) -->
                <BaseButton
                    v-if="showAssignButton"
                    theme="secondary"
                    size="sm"
                    class="shrink-0 inline-flex items-center gap-1"
                    aria-label="Assign this goal"
                    @click.stop="emit('assign', goal.id)">
                    <i class="bi bi-person-plus text-sm leading-none" aria-hidden="true"></i>
                </BaseButton>

                <!-- Dive Deeper CTA: shown on prioritized goals -->
                <BaseButton
                    v-if="showDiscussButton"
                    theme="secondary"
                    size="sm"
                    class="shrink-0"
                    :class="{ 'pointer-events-none': isDiscussLoading }"
                    aria-label="Dive deeper into this goal with Nadia"
                    @click.stop="emit('discuss', goal.id)">
                    <span class="relative inline-flex items-center justify-center">
                        <LoadingSpinner v-if="isDiscussLoading" class="h-4 w-4 absolute" />
                        <span :class="{ invisible: isDiscussLoading }">Dive Deeper</span>
                    </span>
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type Goal } from "~vue/types/goals";
import { computed } from "vue";

const props = defineProps<{
    goal: Goal;
    isLoading?: boolean;
    isSelected?: boolean;
    isSuggestion?: boolean;
    isLinkedHover?: boolean;
    showAssignButton?: boolean;
    showDiscussButton?: boolean;
    isDiscussLoading?: boolean;
}>();

const emit = defineEmits<{
    updated: [goalId: string];
    select: [goal: Goal];
    assign: [goalId: string];
    discuss: [goalId: string];
}>();

function handleCardActivate(event: Event) {
    const target = event.target as Element | null;
    if (target?.closest("button")) {
        return;
    }
    emit("select", props.goal);
}

const hasCta = computed(() => {
    if (props.showAssignButton) {
        return true;
    }
    if (props.showDiscussButton) {
        return true;
    }
    return false;
});

const suggestionLabel = computed(() => (props.goal.source === "assessment" ? "Suggested from reflection" : "Suggested from chat"));

const completedActions = computed(() => props.goal.actions?.filter((a) => a.status === "completed").length ?? 0);
const totalActions = computed(() => props.goal.actions?.length ?? 0);
const actionLabel = computed(() => {
    const total = totalActions.value;
    const noun = total === 1 ? "action" : "actions";
    return total === 0 ? "No actions for this goal" : `${completedActions.value}/${total} ${noun} completed`;
});
</script>
